/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    Autocomplete,
    Box,
    Button,
    Grid,
    Modal,
    Fade,
    Backdrop,
    Stack,
    Typography,
    useTheme,
    TextField,
    Tooltip,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Alert,
} from "@mui/material";

import { DetailedUser, Effect, ExpandedWorkOrder, ModalBase, Nullable, User } from "../../utils/Interfaces";
import { assigneesInclude, createTechnicianEntry, getAllTechnicians } from "./workOrderFunctions";
import { customScrollbarStyle, delayedAction, filterCollection } from "../../utils/Functions";
import { Add } from "@mui/icons-material";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useWindowDimensions } from "../../utils/Hooks";
import CloseIcon from "@mui/icons-material/Close";

export interface CreateTechnicianEntryModalProps extends ModalBase {
    workOrder: ExpandedWorkOrder;
    onSubmit: Effect;
}

const CreateTechnicianEntryModal = (props: CreateTechnicianEntryModalProps) => {
    const DEFAULT_TASKS = ["Hook NACS/Sirens Back Up", "Reset Panel", "Keypad Clear", "Take Store off Test"];

    const { open, handleClose, workOrder, onSubmit } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();
    const mobile: boolean = width < 600;

    const [comments, setComments] = useState<Nullable<string>>("");
    const [assignees, setAssignees] = useState<DetailedUser[]>([]);
    const [tasks, setTasks] = useState<string[]>([...DEFAULT_TASKS]);
    const [allAssignees, setAllAssignees] = useState<DetailedUser[]>([]);
    const [selectedAssignee, setSelectedAssignee] = useState<Nullable<DetailedUser>>(null);
    const [selectedTask, setSelectedTask] = useState<string>("");
    const [warnUnaddedTask, setWarnUnaddedTask] = useState<boolean>(false);

    const [processing, setProcessing] = useState<boolean>(false);

    const theme = useTheme();

    function assign(assignee: DetailedUser) {
        if (!assigneesInclude(assignee, assignees)) {
            setAssignees([...assignees, assignee]);
        }
        setSelectedAssignee(null);
    }

    function unassign(toUnassign: DetailedUser) {
        setAssignees(assignees.filter((user: DetailedUser) => user.id !== toUnassign.id));
    }

    function addTask(task: string) {
        tasks.push(task);
    }

    function removeTask(task: string) {
        setTasks(tasks.filter((tsk: string) => tsk !== task));
    }

    function close() {
        handleClose();
        void delayedAction(500, () => {
            setProcessing(false);
            setAssignees([]);
            setTasks([...DEFAULT_TASKS]);
            setComments("");
            setSelectedTask("");
            setWarnUnaddedTask(false);
        });
    }

    function submit() {
        setProcessing(true);
        createTechnicianEntry(workOrder.id, assignees, tasks, comments, dispatch, () => {
            onSubmit();
            close();
        });
    }

    useEffect(() => {
        if (open) {
            getAllTechnicians(dispatch, setAllAssignees);
        }
    }, [dispatch, open]);

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={close}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        width: mobile ? "100%" : "60%",
                        zIndex: 1000,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant={mobile ? "h6" : "h5"} textAlign="left" sx={{ mb: 2 }}>
                            Create New Trip for Work Order
                        </Typography>
                        <Stack direction="column" justifyContent="flex-start">
                            <Tooltip title="Close">
                                <IconButton onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            pr: 2,
                            maxHeight: "75vh",
                            overflowY: "auto",
                            ...customScrollbarStyle(),
                        }}
                    >
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            margin="normal"
                            value={comments}
                            label="Office Notes"
                            size="small"
                            onChange={(event) => setComments(event.target.value)}
                        />

                        <Box sx={{ mb: 2 }} />

                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Assignees
                        </Typography>

                        <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Autocomplete
                                disablePortal
                                onChange={(_event, value) => {
                                    assign(value as DetailedUser);
                                    setSelectedAssignee(null);
                                }}
                                blurOnSelect={true}
                                id="assignee"
                                autoHighlight={true}
                                getOptionKey={(option: User) => option.id}
                                options={filterCollection<DetailedUser, number>(
                                    allAssignees,
                                    assignees,
                                    (assignee: DetailedUser) => assignee.id
                                )}
                                value={selectedAssignee}
                                style={{
                                    width: "100%",
                                    marginBottom: "25px",
                                }}
                                getOptionLabel={(option: User) => `${option.first_name} ${option.last_name}`}
                                renderInput={(params) => <TextField {...params} fullWidth size="small" label="Lookup Assignee" />}
                            />
                        </Grid>

                        {assignees.length === 0 && (
                            <Typography textAlign="center" sx={{ mt: 2 }}>
                                No technicians assigned.
                            </Typography>
                        )}

                        {assignees.length > 0 && (
                            <Stack direction="row" justifyContent="flex-start" flexWrap="wrap">
                                {assignees.map((entry: DetailedUser, key: number) => {
                                    return (
                                        <Chip
                                            key={key}
                                            color="success"
                                            onDelete={() => unassign(entry)}
                                            label={<Typography>{`${entry.first_name} ${entry.last_name}`}</Typography>}
                                            sx={{ mr: 1, mb: 1 }}
                                            style={{ fontWeight: "bold" }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}

                        <Divider sx={{ mt: 3, mb: 3 }} />

                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Define Technician Checklist
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            label="Define checklist task"
                            value={selectedTask}
                            onChange={(event) => setSelectedTask(event.target.value)}
                            onBlur={() => {
                                if (!selectedTask) {
                                    setWarnUnaddedTask(false);
                                }
                            }}
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="Add checklist task">
                                        <IconButton
                                            disabled={!selectedTask}
                                            sx={{ mr: -1 }}
                                            onClick={() => {
                                                addTask(selectedTask);
                                                setWarnUnaddedTask(false);
                                                setSelectedTask("");
                                            }}
                                        >
                                            <Add fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            }}
                        />

                        {warnUnaddedTask && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography>You have entered a task but not added it.</Typography>
                            </Alert>
                        )}

                        {tasks.length === 0 && (
                            <Typography textAlign="center" sx={{ mt: 2 }}>
                                No checklist tasks defined.
                            </Typography>
                        )}

                        {tasks.length > 0 && (
                            <>
                                {tasks.map((task: string, key: number) => {
                                    return (
                                        <Chip
                                            key={key}
                                            color="default"
                                            onDelete={() => removeTask(task)}
                                            label={<Typography textAlign="left">{task}</Typography>}
                                            sx={{ mr: 1, mb: 1 }}
                                            style={{ fontWeight: "bold" }}
                                        />
                                    );
                                })}
                            </>
                        )}

                        <Box sx={{ mb: 5 }} />

                        <Stack direction="row" spacing={2} justifyContent="center">
                            {processing && <CircularProgress size={20} sx={{ mt: 1, mb: 1 }} />}

                            {!processing && (
                                <>
                                    <Tooltip title="Cancel Trip Creation">
                                        <Button onClick={close} variant="contained" color="secondary">
                                            Cancel
                                        </Button>
                                    </Tooltip>
                                    {!warnUnaddedTask && (
                                        <Tooltip title="Create Trip">
                                            <Button
                                                onClick={() => {
                                                    if (selectedTask) {
                                                        setWarnUnaddedTask(true);
                                                        return;
                                                    }
                                                    submit();
                                                }}
                                                variant="contained"
                                                color="primary"
                                            >
                                                Create Trip
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {warnUnaddedTask && (
                                        <Tooltip title="Create without extra class.">
                                            <Button
                                                onClick={() => {
                                                    void delayedAction(500, () => setWarnUnaddedTask(false));
                                                    submit();
                                                }}
                                                variant="contained"
                                                color="warning"
                                            >
                                                Create Anyway
                                            </Button>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default CreateTechnicianEntryModal;
